import { sb } from '../supabase';
import { logAdminAction } from './adminAudit';
import { sendMaabarEmail } from './maabarEmail';
import {
  normalizeSupplierDocStoragePath,
  SUPPLIER_REVIEW_QUEUE_RAW_STATUSES,
  SUPPLIER_PUBLIC_VISIBLE_RAW_STATUSES,
} from './supplierOnboarding';

// Data layer for the Admin Console "Factory Verification" section.
//
// Everything a decision touches lives here so the page stays presentational:
//   • the queue (who is waiting, and whether their file is actually reviewable)
//   • signed URLs for the private supplier-docs bucket
//   • the decision itself — profile status + factory badge + email + audit log
//
// No RPC is involved: guard_profile_sensitive_fields() returns early for
// is_admin_user(), so an admin writes profiles.status directly. Suppliers
// cannot — their only path to 'verification_under_review' is the
// submit_supplier_verification RPC.

const nf = (v) => (v === null || v === undefined ? '' : String(v).trim());

// A value counts as present when it is non-empty. years_experience is an
// integer and 0 is a legitimate answer, so this must not treat 0 as missing —
// the RPC's own test is `is null`, and this mirrors it.
const filled = (v) => v !== null && v !== undefined && String(v).trim() !== '';

// The eight fields submit_supplier_verification() refuses to submit without.
// The STAGE-2 (payout) file — the eight keys submit_supplier_verification
// refuses to submit without. NOT the bar for being reviewed: since 2026-09-18
// that is stage 1 (licence + contact + category, submit_supplier_docs). This
// list is what the reviewer sees still outstanding, not what blocks the queue.
// Kept in this order because that is the order the supplier filled them in.
export const REQUIRED_FIELDS = Object.freeze([
  { key: 'company_name',     ar: 'اسم الشركة',      en: 'Company name' },
  { key: 'country',          ar: 'الدولة',          en: 'Country' },
  { key: 'city',             ar: 'المدينة',         en: 'City' },
  { key: 'trade_link',       ar: 'الرابط التجاري',  en: 'Trade link' },
  { key: 'reg_number',       ar: 'رقم السجل',       en: 'Registration number' },
  { key: 'years_experience', ar: 'سنوات الخبرة',    en: 'Years of experience' },
  { key: 'license_photo',    ar: 'الرخصة التجارية', en: 'Business licence' },
  { key: 'factory_photo',    ar: 'صورة المصنع',     en: 'Factory photo' },
]);

// Single-file document slots. `required` here means required for STAGE 2, the
// payout file (submit_supplier_verification) — it used to read "mirrors the
// RPC", which stopped being unambiguous on 2026-09-18 when a second RPC
// appeared. Stage 1, the visibility bar, needs the licence only; the factory
// photo no longer blocks being reviewed. The other two remain supporting
// evidence for neither bar.
export const DOC_SLOTS = Object.freeze([
  { key: 'license_photo',       required: true,  ar: 'الرخصة التجارية',        en: 'Business licence' },
  { key: 'factory_photo',       required: true,  ar: 'صورة المصنع',            en: 'Factory photo' },
  { key: 'legal_rep_id_photo',  required: false, ar: 'هوية الممثل القانوني',   en: 'Legal representative ID' },
  { key: 'address_proof_photo', required: false, ar: 'إثبات العنوان',          en: 'Address proof' },
]);

// Array-valued media columns, appended after the single slots.
export const DOC_ARRAYS = Object.freeze([
  { key: 'factory_images', ar: 'صور المصنع',      en: 'Factory images' },
  { key: 'factory_videos', ar: 'فيديو المصنع',    en: 'Factory video' },
]);

const VIDEO_EXT = /\.(mp4|mov|m4v|webm|ogg|avi|mkv)(\?|$)/i;
const PDF_EXT = /\.pdf(\?|$)/i;

export function docKind(raw, columnKey) {
  const value = nf(raw);
  if (PDF_EXT.test(value)) return 'pdf';
  if (VIDEO_EXT.test(value) || columnKey === 'factory_videos') return 'video';
  return 'image';
}

// Flat, stable list of every attachment on a verification file. `id` is what the
// per-document review checklist keys off, so it must not change between renders.
export function buildDocList(profile = {}) {
  const docs = [];
  // The verification uploader writes the same file to factory_photo AND
  // factory_images[0], so the raw path is the dedup key: the blocking slot is
  // added first and wins, and the gallery does not show it a second time.
  const seen = new Set();
  for (const slot of DOC_SLOTS) {
    const raw = nf(profile[slot.key]);
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    docs.push({ id: slot.key, raw, columnKey: slot.key, required: slot.required, ar: slot.ar, en: slot.en, kind: docKind(raw, slot.key) });
  }
  for (const group of DOC_ARRAYS) {
    const list = Array.isArray(profile[group.key]) ? profile[group.key].filter(Boolean) : [];
    list.forEach((raw, i) => {
      if (seen.has(nf(raw))) return;
      seen.add(nf(raw));
      docs.push({
        id: `${group.key}:${i}`,
        raw: nf(raw),
        columnKey: group.key,
        required: false,
        ar: list.length > 1 ? `${group.ar} ${i + 1}` : group.ar,
        en: list.length > 1 ? `${group.en} ${i + 1}` : group.en,
        kind: docKind(raw, group.key),
      });
    });
  }
  return docs;
}

// ── The journey ────────────────────────────────────────────────────────────
// What a reviewer actually wants to know before touching a file: how far this
// supplier got, when, and how long he has been stuck where he is.
//
// Only three of the four stages carry a real timestamp, and the fourth is left
// blank rather than guessed:
//   · registered  — profiles.created_at
//   · uploaded    — parsed out of the stored path, which the uploader builds as
//                   `${user.id}/${type}_${Date.now()}_${rand}.ext`. Legacy rows
//                   wrote fully-qualified product-images URLs instead, and those
//                   yield nothing; they show as "date unknown", never as today.
//   · submitted   — KNOWN to have happened (the status says so) but NOT when:
//                   submit_supplier_verification() writes no audit row. Recorded
//                   in docs/TECH_DEBT.md rather than papered over here.
//   · decided     — audit_log, which is written by applyVerificationDecision
const DOC_PATH_TS = /_(\d{13})_/;

export function parseDocUploadedAt(raw) {
  const m = DOC_PATH_TS.exec(nf(raw));
  if (!m) return null;
  const ts = Number(m[1]);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const DECISION_ACTIONS = new Set(['supplier_verified', 'supplier_rejected', 'supplier_more_info_required']);

export function buildSupplierJourney(profile = {}, history = []) {
  const docs = buildDocList(profile);
  const uploadTimes = docs.map((d) => parseDocUploadedAt(d.raw)).filter(Boolean).sort();
  const status = nf(profile.status).toLowerCase();
  // Only the CANONICAL status proves a submission: it is the one thing
  // submit_supplier_verification() sets, and that RPC refuses without a licence
  // and a factory photo. A verified account must have passed through it too.
  //
  // The legacy statuses ('pending', 'under_review', 'submitted', 'review') prove
  // nothing — they predate the RPC and mostly carry no documents at all, which is
  // why bucketOf() sorts them as 'incomplete'. Counting them as submitted showed
  // "uploaded: never" above "submitted for review: done" on the same card, which
  // is not a rendering slip but a claim the data does not support.
  const submitted = VERIFIED_STATUSES.includes(status) || status === 'verification_under_review';
  const legacyUnknown = !submitted && REVIEW_STATUSES.includes(status);
  const lastDecision = (history || []).find((h) => DECISION_ACTIONS.has(h.action)) || null;

  const stages = [
    { key: 'registered', at: profile.created_at || null, done: Boolean(profile.created_at) },
    { key: 'uploaded', at: uploadTimes[0] || null, done: docs.length > 0, count: docs.length },
    // `at` is deliberately null even when done: the submit RPC records no time.
    { key: 'submitted', at: null, done: submitted, timeUnknown: submitted, legacyUnknown },
    { key: 'decided', at: lastDecision?.created_at || null, done: Boolean(lastDecision), action: lastDecision?.action || null },
  ];

  // How long he has sat where he is now — the number that decides who to chase.
  const lastKnown = [lastDecision?.created_at, uploadTimes[uploadTimes.length - 1], profile.created_at]
    .filter(Boolean)
    .map((v) => new Date(v).getTime())
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => b - a)[0] || null;
  const stuckDays = lastKnown ? Math.max(0, Math.floor((Date.now() - lastKnown) / 86400000)) : null;

  return { stages, stuckDays, docCount: docs.length, hasSubmitted: submitted, lastDecision };
}

export function buildCompleteness(profile = {}) {
  const items = REQUIRED_FIELDS.map((f) => ({ ...f, ok: filled(profile[f.key]) }));
  const done = items.filter((i) => i.ok).length;
  return { items, done, total: items.length, percent: Math.round((done / items.length) * 100), isComplete: done === items.length };
}

// A file is reviewable when the VISIBILITY bar is met: the business licence is
// attached. Legacy 'pending' rows predate the submit RPC and mostly carry no
// documents at all — they are queued but there is nothing to look at, so they
// get their own bucket instead of padding the real queue.
//
// factory_photo was dropped from this test on 2026-09-18 with the two-bar model
// (migration 20260918183522, and CLAUDE.md golden rule 2). It belongs to the
// stage-2 PAYOUT file, and requiring it here kept every supplier arriving from
// a public RFQ link out of "Ready to review" and parked in "Incomplete" — the
// wrong lane for people whose quotes are waiting on a decision. Measured effect
// when this changed: exactly 2 existing suppliers moved Incomplete → Ready.
//
// ⚠️ PAIRED WITH fetchReadyCount() BELOW. The two encode the same test in two
// places — this one in JS over fetched rows, that one as a Postgres filter for
// the sidebar badge — and they must move together. A badge that counts a
// different set than the tab it points at is a bug that looks like a data
// problem.
export function isReviewable(profile = {}) {
  return Boolean(nf(profile.license_photo));
}

// ── Storage ────────────────────────────────────────────────────────────────
// supplier-docs is a PRIVATE bucket, so every path needs signing. Two upload
// codepaths historically wrote to factory_images with different formats: the
// verification flow stores supplier-docs paths, while the older settings-tab
// uploader stored fully-qualified public URLs to product-images. Passing the
// second form through unchanged is what keeps those rows rendering.
export async function signSupplierDoc(raw, ttlSeconds = 60 * 60) {
  const value = nf(raw);
  if (!value) return null;
  if (/^https?:\/\//i.test(value) && !value.includes('/supplier-docs/')) return value;
  const path = normalizeSupplierDocStoragePath(value);
  if (!path) return null;
  const { data, error } = await sb.storage.from('supplier-docs').createSignedUrl(path, ttlSeconds);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function signAllDocs(docs = [], ttlSeconds = 60 * 60) {
  const entries = await Promise.all(docs.map(async (d) => [d.id, await signSupplierDoc(d.raw, ttlSeconds)]));
  return Object.fromEntries(entries.filter(([, url]) => url));
}

// ── Queue ──────────────────────────────────────────────────────────────────
const REVIEW_STATUSES = SUPPLIER_REVIEW_QUEUE_RAW_STATUSES.map((s) => s.toLowerCase());
const VERIFIED_STATUSES = SUPPLIER_PUBLIC_VISIBLE_RAW_STATUSES.map((s) => s.toLowerCase());

// Registered, but nothing submitted yet. These statuses had NO bucket, and
// bucketOf's callers drop a row without one — so on 2026-09-18 eight of the
// thirty supplier accounts in production (five `registered`, three
// `verification_required`) were invisible on the verification screen entirely.
// They are precisely the people the "action required" card was built to chase:
// the ones who have not started. A queue that cannot show them cannot be the
// place you chase them from.
const NOT_STARTED_STATUSES = ['registered', 'verification_required', 'draft', 'incomplete'];

export function bucketOf(profile = {}) {
  const status = nf(profile.status).toLowerCase();
  if (VERIFIED_STATUSES.includes(status)) return 'verified';
  if (status === 'rejected') return 'rejected';
  if (REVIEW_STATUSES.includes(status)) return isReviewable(profile) ? 'ready' : 'incomplete';
  // Deliberately a named list, not a catch-all `return 'not_started'`: an
  // inactive or suspended account is not "has not started", and filing it there
  // would put a disabled account in a chase-them queue.
  if (NOT_STARTED_STATUSES.includes(status)) return 'not_started';
  return null;
}

export const QUEUE_TABS = Object.freeze([
  { key: 'ready',       ar: 'جاهز للمراجعة', en: 'Ready to review' },
  { key: 'incomplete',  ar: 'ناقص',          en: 'Incomplete' },
  { key: 'not_started', ar: 'لم يبدأ',        en: 'Not started' },
  { key: 'verified',    ar: 'موثّق',          en: 'Verified' },
  { key: 'rejected',    ar: 'مرفوض',          en: 'Rejected' },
]);

const QUEUE_COLUMNS =
  'id, full_name, company_name, company_name_latin, email, lang, phone, whatsapp, wechat, ' +
  'country, city, speciality, business_type, status, maabar_supplier_id, avatar_url, ' +
  'trade_link, company_website, company_description, reg_number, company_reg_number, ' +
  'years_experience, export_years, num_employees, export_markets, certifications, ' +
  'license_photo, factory_photo, legal_rep_id_photo, address_proof_photo, ' +
  'factory_images, factory_videos, created_at';

// One round-trip set for the whole screen: supplier profiles + the factory rows
// that link to them (a claimed factory carries the buyer-facing badge) + product
// counts. Admins read all three directly under is_admin_user() RLS.
export async function fetchVerificationQueue() {
  const [{ data: sups, error: supErr }, { data: facs }, { data: prods }] = await Promise.all([
    sb.from('profiles').select(QUEUE_COLUMNS).eq('role', 'supplier').order('created_at', { ascending: false }),
    sb.from('factory_directory').select('id, company_name, linked_supplier_id, is_verified, is_active, claimed_at, claim_slug'),
    sb.from('products').select('supplier_id'),
  ]);
  if (supErr) throw supErr;

  const factoryByOwner = {};
  for (const f of facs || []) if (f.linked_supplier_id) factoryByOwner[f.linked_supplier_id] = f;

  const productCount = {};
  for (const p of prods || []) if (p.supplier_id) productCount[p.supplier_id] = (productCount[p.supplier_id] || 0) + 1;

  // A duplicate company name across accounts is worth seeing while deciding —
  // it is how a supplier who registered twice (once as a buyer) shows up.
  const nameCount = {};
  for (const s of sups || []) {
    const key = nf(s.company_name).toLowerCase();
    if (key) nameCount[key] = (nameCount[key] || 0) + 1;
  }

  const rows = (sups || []).map((s) => {
    const factory = factoryByOwner[s.id] || null;
    return {
      ...s,
      bucket: bucketOf(s),
      factory,
      origin: factory ? 'factory_claim' : 'organic',
      productCount: productCount[s.id] || 0,
      docCount: buildDocList(s).length,
      completeness: buildCompleteness(s),
      duplicateName: (nameCount[nf(s.company_name).toLowerCase()] || 0) > 1,
    };
  }).filter((r) => r.bucket);

  const buckets = { ready: [], incomplete: [], not_started: [], verified: [], rejected: [] };
  for (const r of rows) buckets[r.bucket].push(r);
  return { rows, buckets };
}

// Cheap count for the sidebar badge — the same "ready" test as bucketOf(),
// expressed as a filter so it never pulls rows down just to count them.
//
// ⚠️ PAIRED WITH isReviewable() ABOVE — same test, two expressions, and they
// must move together. The factory_photo filter was dropped here on 2026-09-18
// for the same reason it was dropped there: it is the stage-2 payout bar, not
// the stage-1 visibility bar.
export async function fetchReadyCount() {
  const { count, error } = await sb.from('profiles')
    .select('id', { count: 'exact', head: true })
    .eq('role', 'supplier')
    .in('status', REVIEW_STATUSES)
    .not('license_photo', 'is', null);
  if (error) return 0;
  return count || 0;
}

// The supplier's products, read-only, for the reviewer.
//
// Not a management screen — activating a variant or fixing stock is product work
// and lives elsewhere. What a verification reviewer needs is the cross-check: a
// factory whose stated speciality is furniture and whose catalogue is phone cases
// is a question worth asking BEFORE the badge goes on, not after.
export async function fetchSupplierProducts(supplierId, limit = 12) {
  const { data, error } = await sb
    .from('products')
    .select('id, name_en, name_zh, name_ar, moq, is_active, has_variants, created_at')
    .eq('supplier_id', supplierId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    // Security rule 4 applies to our own backend too: a swallowed read is how a
    // reviewer concludes "no products" about a supplier who has plenty.
    console.error('[fetchSupplierProducts] failed:', error.message || error);
    return { rows: [], failed: true };
  }
  return { rows: data || [], failed: false };
}

export async function fetchReviewHistory(supplierId) {
  const { data, error } = await sb.from('audit_log')
    .select('id, action, notes, before_state, after_state, created_at, actor_id')
    .eq('entity_type', 'supplier').eq('entity_id', supplierId)
    .order('created_at', { ascending: false }).limit(20);
  if (error) return [];
  const actorIds = [...new Set((data || []).map((r) => r.actor_id).filter(Boolean))];
  const names = {};
  if (actorIds.length) {
    const { data: actors } = await sb.from('profiles').select('id, full_name, email').in('id', actorIds);
    for (const a of actors || []) names[a.id] = nf(a.full_name) || nf(a.email) || '';
  }
  return (data || []).map((r) => ({ ...r, actorName: names[r.actor_id] || '' }));
}

// ── Decision ───────────────────────────────────────────────────────────────
export const DECISIONS = Object.freeze({
  approve:   { status: 'verified',              email: 'supplier_approved',           action: 'supplier_verified',           factoryVerified: true,  needsNote: false },
  more_info: { status: 'verification_required', email: 'supplier_more_info_required', action: 'supplier_more_info_required', factoryVerified: false, needsNote: true },
  reject:    { status: 'rejected',              email: 'supplier_rejected',           action: 'supplier_rejected',           factoryVerified: false, needsNote: true },
});

// profiles.verification_review_note may not exist yet (its migration is applied
// deliberately, not by this code). Until it does, the decision must still land —
// the note then survives in audit_log only, and the caller is told so rather
// than the write failing outright.
function isMissingNoteColumn(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const message = `${error.message || ''} ${error.details || ''}`.toLowerCase();
  return code === '42703' || code === 'PGRST204' || message.includes('verification_review_note');
}

/**
 * Apply an admin verification decision, end to end.
 *
 * Order matters: the profile status is the source of truth, so it is written
 * first and a failure there aborts everything. The factory badge, the email and
 * the audit entry are follow-ups — each is reported in `warnings` if it fails,
 * because a silently swallowed email is how a supplier ends up verified and
 * never told.
 */
export async function applyVerificationDecision({ supplier, decision, note = '', actorId }) {
  const spec = DECISIONS[decision];
  if (!spec) throw new Error(`Unknown verification decision: ${decision}`);
  const trimmedNote = nf(note);
  if (spec.needsNote && !trimmedNote) throw new Error('A note is required for this decision.');

  const warnings = [];
  const before = { status: supplier.status, is_verified: supplier.factory?.is_verified ?? null };
  let noteStored = true;

  let res = await sb.from('profiles')
    .update({ status: spec.status, verification_review_note: trimmedNote || null })
    .eq('id', supplier.id).select('id, status');

  if (res.error && isMissingNoteColumn(res.error)) {
    noteStored = false;
    res = await sb.from('profiles').update({ status: spec.status }).eq('id', supplier.id).select('id, status');
  }
  if (res.error) throw res.error;
  if (!res.data || res.data.length === 0) throw new Error('No profile row was updated — check admin RLS.');
  if (!noteStored) warnings.push('note_column_missing');

  // The buyer-facing badge must always match the file: a claimed factory keeps
  // its own is_verified flag, and it is the one traders actually see.
  if (supplier.factory?.id && supplier.factory.is_verified !== spec.factoryVerified) {
    const { error } = await sb.from('factory_directory')
      .update({ is_verified: spec.factoryVerified, updated_at: new Date().toISOString(), updated_by: actorId })
      .eq('id', supplier.factory.id);
    if (error) warnings.push('factory_badge_failed');
  }

  if (nf(supplier.email)) {
    try {
      await sendMaabarEmail({
        type: spec.email,
        to: supplier.email,
        data: { name: nf(supplier.full_name) || nf(supplier.company_name) || '', lang: nf(supplier.lang) || 'en', note: trimmedNote },
      });
    } catch (e) {
      console.error('[consoleVerification] decision email failed:', e?.message || e);
      warnings.push('email_failed');
    }
  } else {
    warnings.push('no_email');
  }

  await logAdminAction({
    actorId,
    action: spec.action,
    entityType: 'supplier',
    entityId: supplier.id,
    beforeState: before,
    afterState: { status: spec.status, is_verified: supplier.factory ? spec.factoryVerified : null },
    notes: trimmedNote || null,
  });

  return { status: spec.status, warnings };
}
